'use client';

import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  ReferenceLine
} from 'recharts';

interface MACDData {
  dif: number;
  dea: number;
  macd: number;
  signal: 'bullish' | 'bearish' | 'neutral';
  difSlope: number;
  difSlopeAbs: number;
  secondOrderSlope: number;
  slopeSignal: 'accelerating' | 'decelerating' | 'stable';
  trendStrength: 'strong' | 'moderate' | 'weak';
  slopeDirection: 'up' | 'down' | 'flat';
  macdSlope: number;
  macdSecondSlope: number;
}

interface PriceDataPoint {
  time: number;
  price: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  // 箱图字段
  q1?: number;
  median?: number;
  q3?: number;
  iqr?: number;
  lowerWhisker?: number;
  upperWhisker?: number;
  dif?: number;
  dea?: number;
  macd?: number;
  signal?: 'bullish' | 'bearish' | 'neutral';
  difSlope?: number;
  difSlopeAbs?: number;
  secondOrderSlope?: number;
  slopeSignal?: 'accelerating' | 'decelerating' | 'stable';
  trendStrength?: 'strong' | 'moderate' | 'weak';
  slopeDirection?: 'up' | 'down' | 'flat';
  macdSlope?: number;
  macdSecondSlope?: number;
  // BOLL带字段
  bollMiddle?: number;  // BOLL中轨（VWAP）
  bollUpper?: number;   // BOLL上轨
  bollLower?: number;   // BOLL下轨
  bandwidth?: number;   // 带宽
  percentB?: number;    // %B指标
}

interface EthPriceData {
  currentPrice: number;
  change24h: number;
  changePercent24h: number;
  chartData: PriceDataPoint[];
  currentMacd: MACDData | null;
}

type TimeRange = 'ALL' | '72H' | '24H' | '12H' | '5H';

// 自定义价格 Tooltip
function PriceTooltip({ active, payload, t }: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const date = new Date(data.time);
    const hasBoxPlot = data.q1 !== undefined && data.median !== undefined && data.q3 !== undefined;
    const hasOHLC = data.open !== undefined && !hasBoxPlot;

    return (
      <div className="rounded border border-white/20 bg-black px-3 py-2 font-mono text-[10px]">
        <p className="text-gray-500 mb-1.5">
          {date.toLocaleString('zh-CN', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
        {hasBoxPlot ? (
          <div className="space-y-0.5">
            <p className="text-purple-400">
              Max: ${data.high.toFixed(2)}
            </p>
            <p className="text-blue-400">
              Q3: ${data.q3.toFixed(2)}
            </p>
            <p className="text-white font-semibold">
              Median: ${data.median.toFixed(2)}
            </p>
            <p className="text-orange-400">
              Q1: ${data.q1.toFixed(2)}
            </p>
            <p className="text-red-400">
              Min: ${data.low.toFixed(2)}
            </p>
            {data.upperWhisker !== undefined && data.lowerWhisker !== undefined && (
              <>
                <div className="my-1 border-t border-white/10" />
                <p className="text-gray-400 text-[9px]">
                  Upper Whisker: ${data.upperWhisker.toFixed(2)}
                </p>
                <p className="text-gray-400 text-[9px]">
                  Lower Whisker: ${data.lowerWhisker.toFixed(2)}
                </p>
              </>
            )}
          </div>
        ) : hasOHLC ? (
          <div className="space-y-0.5">
            <p className="text-white">
              {t.ethPrice.open}: ${data.open.toFixed(2)}
            </p>
            <p className="text-blue-400">
              {t.ethPrice.high}: ${data.high.toFixed(2)}
            </p>
            <p className="text-orange-400">
              {t.ethPrice.low}: ${data.low.toFixed(2)}
            </p>
            <p className={data.close >= data.open ? 'text-emerald-400' : 'text-red-400'}>
              {t.ethPrice.close}: ${data.close.toFixed(2)}
            </p>
          </div>
        ) : (
          <p className="font-semibold text-white">
            Price: ${data.price.toFixed(2)}
          </p>
        )}
        {/* BOLL带信息 */}
        {data.bollMiddle !== undefined && (
          <>
            <div className="my-1.5 border-t border-white/10" />
            <div className="space-y-0.5">
              <p className="text-[9px] text-gray-400 mb-1">BOLL Bands (VWAP)</p>
              <p className="text-red-300 text-[9px]">
                Upper: ${data.bollUpper?.toFixed(2)}
              </p>
              <p className="text-yellow-300 text-[9px] font-semibold">
                Middle (VWAP): ${data.bollMiddle.toFixed(2)}
              </p>
              <p className="text-green-300 text-[9px]">
                Lower: ${data.bollLower?.toFixed(2)}
              </p>
              {data.percentB !== undefined && (
                <p className="text-gray-400 text-[9px]">
                  %B: {(data.percentB * 100).toFixed(1)}%
                </p>
              )}
            </div>
          </>
        )}
      </div>
    );
  }
  return null;
}

// 自定义 MACD Tooltip
function MacdTooltip({ active, payload, t }: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const date = new Date(data.time);

    return (
      <div className="rounded border border-white/20 bg-black px-3 py-2 font-mono text-[10px]">
        <p className="text-gray-500 mb-1.5">
          {date.toLocaleString('zh-CN', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
        {data.dif !== undefined && (
          <p className="font-semibold text-blue-400">
            {t.ethPrice.macdDif}: {data.dif.toFixed(4)}
          </p>
        )}
        {data.difSlope !== undefined && (
          <p className="font-semibold" style={{ color: data.difSlope >= 0 ? '#10b981' : '#ef4444' }}>
            {t.ethPrice.firstOrderSlope}: {data.difSlope >= 0 ? '+' : ''}{data.difSlope.toFixed(6)}
          </p>
        )}
        {data.secondOrderSlope !== undefined && (
          <p className="font-semibold" style={{ color: data.secondOrderSlope >= 0 ? '#10b981' : '#ef4444' }}>
            {t.ethPrice.secondOrderSlope}: {data.secondOrderSlope >= 0 ? '+' : ''}{data.secondOrderSlope.toFixed(6)}
          </p>
        )}
        {/* 添加分隔线 */}
        {data.macd !== undefined && <div className="my-1.5 border-t border-white/10" />}
        {/* MACD值 */}
        {data.macd !== undefined && (
          <p className="font-semibold text-yellow-400">
            {t.ethPrice.macdBar}: {data.macd.toFixed(4)}
          </p>
        )}
        {/* MACD一阶斜率 */}
        {data.macdSlope !== undefined && (
          <p className="font-semibold" style={{ color: data.macdSlope >= 0 ? '#10b981' : '#ef4444' }}>
            {t.ethPrice.macdFirstOrderSlope}: {data.macdSlope >= 0 ? '+' : ''}{data.macdSlope.toFixed(6)}
          </p>
        )}
        {/* MACD二阶斜率 */}
        {data.macdSecondSlope !== undefined && (
          <p className="font-semibold" style={{ color: data.macdSecondSlope >= 0 ? '#10b981' : '#ef4444' }}>
            {t.ethPrice.macdSecondOrderSlope}: {data.macdSecondSlope >= 0 ? '+' : ''}{data.macdSecondSlope.toFixed(6)}
          </p>
        )}
      </div>
    );
  }
  return null;
}

// 计算四分位数
function calculateQuartiles(sortedPrices: number[]): { q1: number; median: number; q3: number } {
  const len = sortedPrices.length;
  if (len === 0) return { q1: 0, median: 0, q3: 0 };
  if (len === 1) {
    const val = sortedPrices[0];
    return { q1: val, median: val, q3: val };
  }
  
  // 计算中位数
  const median = len % 2 === 0
    ? (sortedPrices[len / 2 - 1] + sortedPrices[len / 2]) / 2
    : sortedPrices[Math.floor(len / 2)];
  
  // 分割数据
  const lowerHalf = sortedPrices.slice(0, Math.floor(len / 2));
  const upperHalf = sortedPrices.slice(Math.ceil(len / 2));
  
  // 计算Q1
  let q1: number;
  if (lowerHalf.length === 0) {
    q1 = sortedPrices[0];
  } else if (lowerHalf.length === 1) {
    q1 = lowerHalf[0];
  } else {
    q1 = lowerHalf.length % 2 === 0
      ? (lowerHalf[lowerHalf.length / 2 - 1] + lowerHalf[lowerHalf.length / 2]) / 2
      : lowerHalf[Math.floor(lowerHalf.length / 2)];
  }
  
  // 计算Q3
  let q3: number;
  if (upperHalf.length === 0) {
    q3 = sortedPrices[len - 1];
  } else if (upperHalf.length === 1) {
    q3 = upperHalf[0];
  } else {
    q3 = upperHalf.length % 2 === 0
      ? (upperHalf[upperHalf.length / 2 - 1] + upperHalf[upperHalf.length / 2]) / 2
      : upperHalf[Math.floor(upperHalf.length / 2)];
  }
  
  return { q1, median, q3 };
}

// 将OHLCV K线数据转换为箱图数据
// 对于K线数据，我们使用OHLC来创建箱图：
// - High = 最大值
// - Q3 = max(Open, Close)
// - Median = (Open + Close) / 2
// - Q1 = min(Open, Close)
// - Low = 最小值
function convertOHLCToBoxPlot(
  candles: Array<{time: number, open: number, high: number, low: number, close: number, volume?: number}>,
  macdHistory?: Array<{
    time: number;
    dif?: number;
    dea?: number;
    macd?: number;
    signal?: 'bullish' | 'bearish' | 'neutral';
    difSlope?: number;
    difSlopeAbs?: number;
    secondOrderSlope?: number;
    slopeSignal?: 'accelerating' | 'decelerating' | 'stable';
    trendStrength?: 'strong' | 'moderate' | 'weak';
    slopeDirection?: 'up' | 'down' | 'flat';
    macdSlope?: number;
    macdSecondSlope?: number;
  }>,
  bollHistory?: Array<{
    time: number;
    middle: number;
    upper: number;
    lower: number;
    close: number;
    bandwidth: number;
    percentB: number;
  }>
): PriceDataPoint[] {
  if (candles.length === 0) return [];

  // 创建MACD数据的时间映射，用于快速查找
  const macdMap = new Map<number, NonNullable<typeof macdHistory>[0]>();
  if (macdHistory && macdHistory.length > 0) {
    macdHistory.forEach(macd => {
      macdMap.set(macd.time, macd);
    });
  }

  // 创建BOLL带数据的时间映射，用于快速查找
  const bollMap = new Map<number, NonNullable<typeof bollHistory>[0]>();
  if (bollHistory && bollHistory.length > 0) {
    bollHistory.forEach(boll => {
      bollMap.set(boll.time, boll);
    });
  }

  return candles.map(candle => {
    const { time, open, high, low, close } = candle;
    
    // 使用OHLC数据创建箱图统计
    // Q3 = max(Open, Close) - 价格区间的上四分位
    // Q1 = min(Open, Close) - 价格区间的下四分位
    // Median = (Open + Close) / 2 - 开盘和收盘的平均值
    const q3 = Math.max(open, close);
    const q1 = Math.min(open, close);
    const median = (open + close) / 2;
    const iqr = q3 - q1;
    
    // 须线延伸到High和Low
    const upperWhisker = high;
    const lowerWhisker = low;
    
    // 查找匹配的MACD数据（允许时间差在60秒内）
    let macdData = macdMap.get(time);
    if (!macdData && macdHistory && macdHistory.length > 0) {
      // 如果精确匹配失败，查找最接近的时间点（在60秒内）
      const timeDiff = 60 * 1000; // 60秒
      for (const macd of macdHistory) {
        if (Math.abs(macd.time - time) <= timeDiff) {
          macdData = macd;
          break;
        }
      }
    }

    // 查找匹配的BOLL带数据（允许时间差在60秒内）
    let bollData = bollMap.get(time);
    if (!bollData && bollHistory && bollHistory.length > 0) {
      // 如果精确匹配失败，查找最接近的时间点（在60秒内）
      const timeDiff = 60 * 1000; // 60秒
      for (const boll of bollHistory) {
        if (Math.abs(boll.time - time) <= timeDiff) {
          bollData = boll;
          break;
        }
      }
    }

    return {
      time,
      price: close, // 使用收盘价作为主要价格
      open,
      high,
      low,
      close,
      // 箱图特有字段
      q1,
      median,
      q3,
      iqr,
      lowerWhisker,
      upperWhisker,
      // MACD数据从历史数据中获取
      dif: macdData?.dif,
      dea: macdData?.dea,
      macd: macdData?.macd,
      signal: macdData?.signal,
      difSlope: macdData?.difSlope,
      difSlopeAbs: macdData?.difSlopeAbs,
      secondOrderSlope: macdData?.secondOrderSlope,
      slopeSignal: macdData?.slopeSignal,
      trendStrength: macdData?.trendStrength,
      slopeDirection: macdData?.slopeDirection,
      macdSlope: macdData?.macdSlope,
      macdSecondSlope: macdData?.macdSecondSlope,
      // BOLL带数据从历史数据中获取
      bollMiddle: bollData?.middle,
      bollUpper: bollData?.upper,
      bollLower: bollData?.lower,
      bandwidth: bollData?.bandwidth,
      percentB: bollData?.percentB
    };
  });
}

// 将价格数据转换为箱图数据（用于单个价格点的情况）
function convertToBoxPlotData(data: PriceDataPoint[], intervalMinutes: number = 0.25): PriceDataPoint[] {
  if (data.length === 0) return [];

  const intervalMs = intervalMinutes * 60 * 1000;
  const boxPlots: PriceDataPoint[] = [];
  
  // 按时间段分组
  const grouped = new Map<number, PriceDataPoint[]>();
  
  data.forEach(point => {
    const intervalStart = Math.floor(point.time / intervalMs) * intervalMs;
    if (!grouped.has(intervalStart)) {
      grouped.set(intervalStart, []);
    }
    grouped.get(intervalStart)!.push(point);
  });
  
  // 为每个时间段计算箱图统计
  grouped.forEach((points, intervalStart) => {
    if (points.length === 0) return;
    
    const prices = points.map(p => p.price).sort((a, b) => a - b);
    const min = prices[0];
    const max = prices[prices.length - 1];
    const { q1, median, q3 } = calculateQuartiles(prices);
    
    // 计算IQR（四分位距）
    const iqr = q3 - q1;
    const lowerWhisker = Math.max(min, q1 - 1.5 * iqr);
    const upperWhisker = Math.min(max, q3 + 1.5 * iqr);
    
    // 使用最后一个点的 MACD 数据
    const lastPoint = points[points.length - 1];
    
    boxPlots.push({
      time: intervalStart,
      price: median, // 使用中位数作为主要价格
      open: min, // 用于兼容性，实际使用 min
      high: max, // 最大值
      low: min, // 最小值
      close: median, // 用于兼容性，实际使用 median
      // 箱图特有字段
      q1,
      median,
      q3,
      iqr,
      lowerWhisker,
      upperWhisker,
      dif: lastPoint.dif,
      dea: lastPoint.dea,
      macd: lastPoint.macd,
      signal: lastPoint.signal,
      difSlope: lastPoint.difSlope,
      difSlopeAbs: lastPoint.difSlopeAbs,
      secondOrderSlope: lastPoint.secondOrderSlope,
      slopeSignal: lastPoint.slopeSignal,
      trendStrength: lastPoint.trendStrength,
      slopeDirection: lastPoint.slopeDirection,
      macdSlope: lastPoint.macdSlope,
      macdSecondSlope: lastPoint.macdSecondSlope
    });
  });
  
  return boxPlots.sort((a, b) => a.time - b.time);
}

export function EthPriceChart() {
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState<TimeRange>('5H');
  const [ethData, setEthData] = useState<EthPriceData>({
    currentPrice: 0,
    change24h: 0,
    changePercent24h: 0,
    chartData: [],
    currentMacd: null
  });
  const [candles, setCandles] = useState<Array<{time: number, open: number, high: number, low: number, close: number, volume?: number}>>([]);
  const [macdHistory, setMacdHistory] = useState<Array<{
    time: number;
    price: number;
    dif?: number;
    dea?: number;
    macd?: number;
    signal?: 'bullish' | 'bearish' | 'neutral';
    difSlope?: number;
    difSlopeAbs?: number;
    secondOrderSlope?: number;
    slopeSignal?: 'accelerating' | 'decelerating' | 'stable';
    trendStrength?: 'strong' | 'moderate' | 'weak';
    slopeDirection?: 'up' | 'down' | 'flat';
    macdSlope?: number;
    macdSecondSlope?: number;
  }>>([]);
  const [selectedPoint, setSelectedPoint] = useState<PriceDataPoint | null>(null);
  const [bollHistory, setBollHistory] = useState<Array<{
    time: number;
    middle: number;
    upper: number;
    lower: number;
    close: number;
    bandwidth: number;
    percentB: number;
  }>>([]);

  // 从 API 获取 ETH K线数据（OHLCV）、MACD 数据和 BOLL带数据
  useEffect(() => {
    const fetchEthData = async () => {
      try {
        // 并行获取K线数据、MACD数据和BOLL带数据
        const [candlesResponse, macdResponse, bollResponse] = await Promise.all([
          fetch('/api/market/eth-candles?interval=1m'),
          fetch('/api/market/eth-history'),
          fetch('/api/market/eth-boll?interval=1m&multiplier=2&useWeighted=true&periodMinutes=11')
        ]);

        const candlesData = await candlesResponse.json();
        const macdData = await macdResponse.json();
        const bollData = await bollResponse.json();

        if (candlesData.success && candlesData.candles) {
          setCandles(candlesData.candles);
          
          // 使用最新的K线数据更新价格信息
          if (candlesData.candles.length > 0) {
            const latestCandle = candlesData.candles[candlesData.candles.length - 1];
            const firstCandle = candlesData.candles[0];
            const change24h = latestCandle.close - firstCandle.open;
            const changePercent24h = firstCandle.open > 0 
              ? (change24h / firstCandle.open) * 100 
              : 0;
            
            setEthData(prev => ({
              ...prev,
              currentPrice: latestCandle.close,
              change24h,
              changePercent24h
            }));
          }
        }

        // 更新MACD数据（如果可用）
        if (macdData.success) {
          if (macdData.currentMacd) {
            setEthData(prev => ({
              ...prev,
              currentMacd: macdData.currentMacd
            }));
          }
          // 保存MACD历史数据
          if (macdData.history && Array.isArray(macdData.history)) {
            setMacdHistory(macdData.history);
          }
        }

        // 更新BOLL带数据（如果可用）
        if (bollData.success && bollData.history && Array.isArray(bollData.history)) {
          setBollHistory(bollData.history);
          console.log(`📊 BOLL data loaded: ${bollData.history.length} points`);
        }
      } catch (error) {
        console.error('Failed to fetch ETH data:', error);
      }
    };

    fetchEthData();
    const interval = setInterval(fetchEthData, 60000); // 每分钟更新一次K线数据
    return () => clearInterval(interval);
  }, []);

  const isPositive = ethData.changePercent24h >= 0;

  const formatXAxis = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatYAxis = (value: number) => {
    return `$${value.toFixed(0)}`;
  };

  // 根据时间范围过滤K线数据并转换为箱图
  const getFilteredData = () => {
    const now = Date.now();
    const ranges: Record<TimeRange, number> = {
      'ALL': 0,
      '72H': 72 * 60 * 60 * 1000,
      '24H': 24 * 60 * 60 * 1000,
      '12H': 12 * 60 * 60 * 1000,
      '5H': 5 * 60 * 60 * 1000
    };

    const rangeMs = ranges[timeRange];
    let filteredCandles = rangeMs === 0 
      ? candles 
      : candles.filter(candle => now - candle.time <= rangeMs);

    // 同时过滤MACD历史数据
    let filteredMacdHistory = rangeMs === 0
      ? macdHistory
      : macdHistory.filter(macd => now - macd.time <= rangeMs);

    // 同时过滤BOLL带历史数据
    let filteredBollHistory = rangeMs === 0
      ? bollHistory
      : bollHistory.filter(boll => now - boll.time <= rangeMs);

    // 使用OHLCV数据转换为箱图（1分钟K线数据），并合并MACD数据和BOLL带数据
    return convertOHLCToBoxPlot(filteredCandles, filteredMacdHistory, filteredBollHistory);
  };

  const filteredData = getFilteredData();

  // 计算MACD图表的统一Y轴域(用于确保DIF/DEA/MACD使用同一个零轴)
  const macdDomain = (() => {
    const allValues = filteredData.flatMap(d => [
      d.dif ?? Infinity,
      d.dea ?? Infinity,
      d.macd ?? Infinity
    ].filter(v => v !== Infinity && !isNaN(v)));

    if (allValues.length === 0) return [0, 0];

    const dataMin = Math.min(...allValues, 0); // 确保包含0
    const dataMax = Math.max(...allValues, 0); // 确保包含0
    const minValue = dataMin < 0 ? dataMin * 1.1 : dataMin * 0.9;
    const maxValue = dataMax > 0 ? dataMax * 1.1 : dataMax * 0.9;

    return [minValue, maxValue];
  })();

  // 处理 DIF 曲线点击
  const handleDifClick = (data: any) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const point = data.activePayload[0].payload;
      setSelectedPoint(point);
    }
  };

  // 获取信号颜色
  const getSignalColor = (signal?: 'bullish' | 'bearish' | 'neutral') => {
    if (signal === 'bullish') return 'text-emerald-400';
    if (signal === 'bearish') return 'text-red-400';
    return 'text-gray-400';
  };

  // 获取信号文本
  const getSignalText = (signal?: 'bullish' | 'bearish' | 'neutral') => {
    if (signal === 'bullish') return t.ethPrice.bullish;
    if (signal === 'bearish') return t.ethPrice.bearish;
    return t.ethPrice.neutral;
  };

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="flex items-end justify-between border-b border-white/10 pb-4">
        <div>
          <h2 className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {t.ethPrice.title}
          </h2>
          <div className="font-mono text-5xl font-bold tracking-tight text-white">
            ${ethData.currentPrice.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </div>
          <div className={`mt-2 font-mono text-sm ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
            {isPositive ? '+' : ''}${ethData.change24h.toFixed(2)} (
            {isPositive ? '+' : ''}{ethData.changePercent24h.toFixed(2)}%)
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="flex gap-2">
          {(['5H', '12H', '24H', '72H', 'ALL'] as TimeRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`rounded border px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider transition-colors ${
                timeRange === range
                  ? 'border-white bg-white text-black'
                  : 'border-white/20 text-gray-500 hover:border-white/40 hover:text-gray-300'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Price Chart - Box Plot */}
      <div className="rounded border border-white/10 bg-zinc-950/50 p-6">
        <h3 className="mb-4 font-mono text-xs font-semibold uppercase tracking-wider text-gray-400">
          {t.ethPrice.currentPrice} ({t.ethPrice.priceBox})
        </h3>
        {filteredData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart
              data={filteredData}
              margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.05)"
                vertical={false}
              />
              <XAxis
                dataKey="time"
                tickFormatter={formatXAxis}
                stroke="rgba(255,255,255,0.2)"
                style={{ fontSize: '10px', fontFamily: 'monospace' }}
                tick={{ fill: 'rgba(255,255,255,0.4)' }}
                tickLine={false}
              />
              <YAxis
                domain={['auto', 'auto']}
                tickFormatter={formatYAxis}
                stroke="rgba(255,255,255,0.2)"
                style={{ fontSize: '10px', fontFamily: 'monospace' }}
                tick={{ fill: 'rgba(255,255,255,0.4)' }}
                tickLine={false}
                width={60}
              />
              <Tooltip content={(props) => <PriceTooltip {...props} t={t} />} />
              {/* 箱图 */}
              <Bar
                dataKey="median"
                shape={(props: any) => {
                  const { x, width, payload, viewBox } = props;
                  if (!payload.q1 || !payload.median || !payload.q3) return <g />;

                  const { low, high, q1, median, q3, lowerWhisker, upperWhisker } = payload;
                  
                  // 获取图表区域信息
                  const chartHeight = viewBox?.height || 250;
                  const chartY = viewBox?.y || 0;
                  
                  // 计算价格范围
                  const allPrices = filteredData.flatMap(d => [
                    d.high || d.price, 
                    d.low || d.price,
                    d.upperWhisker || d.high || d.price,
                    d.lowerWhisker || d.low || d.price
                  ]);
                  const maxPrice = Math.max(...allPrices);
                  const minPrice = Math.min(...allPrices);
                  const priceRange = maxPrice - minPrice;
                  
                  // 手动计算Y坐标（从上到下）
                  const getY = (price: number) => {
                    return chartY + ((maxPrice - price) / priceRange) * chartHeight;
                  };
                  
                  const yMax = getY(high);
                  const yMin = getY(low);
                  const yQ3 = getY(q3);
                  const yMedian = getY(median);
                  const yQ1 = getY(q1);
                  const yUpperWhisker = upperWhisker !== undefined ? getY(upperWhisker) : yMax;
                  const yLowerWhisker = lowerWhisker !== undefined ? getY(lowerWhisker) : yMin;
                  
                  const boxWidth = Math.max(width * 0.5, 4);
                  const centerX = x + width / 2;
                  const whiskerWidth = boxWidth * 0.3;

                  return (
                    <g>
                      {/* 上须线（从Q3到upperWhisker） */}
                      <line
                        x1={centerX}
                        y1={yQ3}
                        x2={centerX}
                        y2={yUpperWhisker}
                        stroke="rgba(255,255,255,0.4)"
                        strokeWidth={1}
                      />
                      {/* 上须线横线 */}
                      <line
                        x1={centerX - whiskerWidth / 2}
                        y1={yUpperWhisker}
                        x2={centerX + whiskerWidth / 2}
                        y2={yUpperWhisker}
                        stroke="rgba(255,255,255,0.4)"
                        strokeWidth={1}
                      />
                      
                      {/* 箱体（Q3到Q1） */}
                      <rect
                        x={centerX - boxWidth / 2}
                        y={yQ3}
                        width={boxWidth}
                        height={Math.max(yQ1 - yQ3, 1)}
                        fill="rgba(59, 130, 246, 0.3)"
                        stroke="#3b82f6"
                        strokeWidth={1}
                      />
                      
                      {/* 中位数线 */}
                      <line
                        x1={centerX - boxWidth / 2}
                        y1={yMedian}
                        x2={centerX + boxWidth / 2}
                        y2={yMedian}
                        stroke="#ffffff"
                        strokeWidth={2}
                      />
                      
                      {/* 下须线（从Q1到lowerWhisker） */}
                      <line
                        x1={centerX}
                        y1={yQ1}
                        x2={centerX}
                        y2={yLowerWhisker}
                        stroke="rgba(255,255,255,0.4)"
                        strokeWidth={1}
                      />
                      {/* 下须线横线 */}
                      <line
                        x1={centerX - whiskerWidth / 2}
                        y1={yLowerWhisker}
                        x2={centerX + whiskerWidth / 2}
                        y2={yLowerWhisker}
                        stroke="rgba(255,255,255,0.4)"
                        strokeWidth={1}
                      />
                      
                      {/* 异常值点（如果有超出须线的值） */}
                      {high > (upperWhisker || high) && (
                        <circle
                          cx={centerX}
                          cy={yMax}
                          r={2}
                          fill="#ef4444"
                        />
                      )}
                      {low < (lowerWhisker || low) && (
                        <circle
                          cx={centerX}
                          cy={yMin}
                          r={2}
                          fill="#ef4444"
                        />
                      )}
                    </g>
                  );
                }}
              />
              {/* BOLL带曲线 */}
              {/* BOLL上轨 */}
              <Line
                type="monotone"
                dataKey="bollUpper"
                stroke="#ef4444"
                strokeWidth={1.5}
                dot={false}
                strokeDasharray="3 3"
                animationDuration={1000}
                connectNulls
              />
              {/* BOLL中轨（VWAP） */}
              <Line
                type="monotone"
                dataKey="bollMiddle"
                stroke="#fbbf24"
                strokeWidth={2}
                dot={false}
                animationDuration={1000}
                connectNulls
              />
              {/* BOLL下轨 */}
              <Line
                type="monotone"
                dataKey="bollLower"
                stroke="#10b981"
                strokeWidth={1.5}
                dot={false}
                strokeDasharray="3 3"
                animationDuration={1000}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[250px] items-center justify-center">
            <div className="text-center font-mono">
              <div className="mb-2 text-2xl text-gray-600">━</div>
              <p className="text-xs text-gray-600 uppercase tracking-wider">
                {t.common.waitingForData}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* MACD Chart */}
      <div className="rounded border border-white/10 bg-zinc-950/50 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-400">
            {t.ethPrice.macdTitle}
          </h3>
          {ethData.currentMacd && (
            <div className="flex items-center gap-3 font-mono text-[10px]">
              <span className={getSignalColor(ethData.currentMacd.signal)}>
                {t.ethPrice.macdSignal}: {getSignalText(ethData.currentMacd.signal)}
              </span>
            </div>
          )}
        </div>
        
        <p className="mb-4 font-mono text-[10px] text-gray-500">
          {t.ethPrice.clickTip}
        </p>

        {filteredData.length > 0 && filteredData.some(d => d.dif !== undefined) ? (
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart
              data={filteredData}
              margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
              onClick={handleDifClick}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.05)"
                vertical={false}
              />
              <XAxis
                dataKey="time"
                tickFormatter={formatXAxis}
                stroke="rgba(255,255,255,0.2)"
                style={{ fontSize: '10px', fontFamily: 'monospace' }}
                tick={{ fill: 'rgba(255,255,255,0.4)' }}
                tickLine={false}
              />
              <YAxis
                yAxisId={0}
                stroke="rgba(255,255,255,0.2)"
                style={{ fontSize: '10px', fontFamily: 'monospace' }}
                tick={{ fill: 'rgba(255,255,255,0.4)' }}
                tickLine={false}
                width={60}
                domain={macdDomain}
                allowDataOverflow={false}
              />
              <Tooltip content={(props) => <MacdTooltip {...props} t={t} />} />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" strokeDasharray="3 3" yAxisId={0} />
              {/* MACD 柱状图 */}
              <Bar
                dataKey="macd"
                fill="#8884d8"
                opacity={0.6}
                yAxisId={0}
                background={{ fill: 'transparent' }}
                shape={(props: any) => {
                  const { x, width, payload, background } = props;
                  const macdValue = payload.macd;

                  if (macdValue === undefined || macdValue === null || !background) {
                    return <g />;
                  }

                  const fill = macdValue >= 0 ? '#10b981' : '#ef4444';

                  // 从 background 获取图表区域信息
                  const chartHeight = background.height;
                  const chartY = background.y;

                  // 使用预计算的domain范围
                  const [minValue, maxValue] = macdDomain;
                  const valueRange = maxValue - minValue;

                  if (valueRange === 0) {
                    return <g />;
                  }

                  // 计算0基线的Y坐标（从图表顶部开始）
                  const zeroY = chartY + ((maxValue - 0) / valueRange) * chartHeight;

                  // 计算当前MACD值的Y坐标
                  const macdY = chartY + ((maxValue - macdValue) / valueRange) * chartHeight;

                  // 计算柱状图的高度和位置
                  const barHeight = Math.abs(zeroY - macdY);
                  // 正值: 从macdY到zeroY; 负值: 从zeroY到macdY
                  const rectY = macdValue >= 0 ? macdY : zeroY;

                  // 确保高度至少为1像素
                  const finalHeight = Math.max(barHeight, 1);
                  const barWidth = Math.max(width * 0.6, 3);
                  const centerX = x + width / 2 - barWidth / 2;

                  return (
                    <rect
                      x={centerX}
                      y={rectY}
                      width={barWidth}
                      height={finalHeight}
                      fill={fill}
                      opacity={0.6}
                    />
                  );
                }}
              />
              {/* DIF 线 */}
              <Line
                type="monotone"
                dataKey="dif"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                animationDuration={1000}
                yAxisId={0}
              />
              {/* DEA 线 */}
              <Line
                type="monotone"
                dataKey="dea"
                stroke="#f97316"
                strokeWidth={2}
                dot={false}
                animationDuration={1000}
                yAxisId={0}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[250px] items-center justify-center">
            <div className="text-center font-mono">
              <div className="mb-2 text-2xl text-gray-600">━</div>
              <p className="text-xs text-gray-600 uppercase tracking-wider">
                Collecting data for MACD calculation...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* MACD Details Card */}
      {ethData.currentMacd && (
        <div className="grid grid-cols-2 gap-4">
          {/* Current MACD Values */}
          <div className="rounded border border-white/10 bg-zinc-950/50 p-4">
            <h3 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t.ethPrice.macdTitle}
            </h3>
            <div className="space-y-2 font-mono text-[11px]">
              <div className="flex justify-between">
                <span className="text-gray-500">{t.ethPrice.macdDif}</span>
                <span className="font-semibold text-blue-400">
                  {ethData.currentMacd.dif.toFixed(4)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t.ethPrice.macdDea}</span>
                <span className="font-semibold text-orange-400">
                  {ethData.currentMacd.dea.toFixed(4)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t.ethPrice.macdBar}</span>
                <span className={`font-semibold ${ethData.currentMacd.macd >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {ethData.currentMacd.macd.toFixed(4)}
                </span>
              </div>
              <div className="flex justify-between border-t border-white/10 pt-2">
                <span className="text-gray-500">{t.ethPrice.macdSignal}</span>
                <span className={getSignalColor(ethData.currentMacd.signal)}>
                  {getSignalText(ethData.currentMacd.signal)}
                </span>
              </div>
            </div>
          </div>

          {/* Slope Details (Current or Selected) */}
          <div className="rounded border border-white/10 bg-zinc-950/50 p-4">
            <h3 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-gray-400">
              {selectedPoint ? 'Selected Point Slope' : 'Current Slope Details'}
            </h3>
            {(() => {
              const data = selectedPoint || ethData.currentMacd;
              if (!data) return null;

              return (
                <div className="space-y-2 font-mono text-[11px]">
                  {/* DIF Slopes */}
                  <div className="text-xs font-semibold text-blue-400 mb-1.5">DIF</div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t.ethPrice.firstOrderSlope}</span>
                    <span className={`font-semibold ${(data.difSlope || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(data.difSlope || 0) >= 0 ? '+' : ''}{(data.difSlope || 0).toFixed(6)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t.ethPrice.secondOrderSlope}</span>
                    <span className={`font-semibold ${(data.secondOrderSlope || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(data.secondOrderSlope || 0) >= 0 ? '+' : ''}{(data.secondOrderSlope || 0).toFixed(6)}
                    </span>
                  </div>
                  
                  {/* MACD Slopes */}
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <div className="text-xs font-semibold text-yellow-400 mb-1.5">MACD</div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.ethPrice.macdFirstOrderSlope}</span>
                      <span className={`font-semibold ${(data.macdSlope || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {(data.macdSlope || 0) >= 0 ? '+' : ''}{(data.macdSlope || 0).toFixed(6)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.ethPrice.macdSecondOrderSlope}</span>
                      <span className={`font-semibold ${(data.macdSecondSlope || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {(data.macdSecondSlope || 0) >= 0 ? '+' : ''}{(data.macdSecondSlope || 0).toFixed(6)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between border-t border-white/10 pt-2">
                    <span className="text-gray-500">{t.ethPrice.slopeDirection}</span>
                    <span className="font-semibold text-white">
                      {data.slopeDirection === 'up' ? t.ethPrice.up : data.slopeDirection === 'down' ? t.ethPrice.down : t.ethPrice.flat}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t.ethPrice.trendStrength}</span>
                    <span className="font-semibold text-white">
                      {data.trendStrength === 'strong' ? t.ethPrice.strong : data.trendStrength === 'moderate' ? t.ethPrice.moderate : t.ethPrice.weak}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t.ethPrice.slopeSignal}</span>
                    <span className="font-semibold text-white">
                      {data.slopeSignal === 'accelerating' ? t.ethPrice.accelerating : data.slopeSignal === 'decelerating' ? t.ethPrice.decelerating : t.ethPrice.stable}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

